import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => (slug ? getArticleBySlug(slug) : undefined), [slug]);
  const relatedArticles = useMemo(
    () => (article ? getRelatedArticles(article) : []),
    [article]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              文章未找到
            </h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '爱妻艺找回',
    },
    publisher: {
      '@type': 'Organization',
      name: '爱妻艺找回',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://aiqiyi-zhaihui.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={`${article.title} - 爱妻艺找回`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://aiqiyi-zhaihui.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema}
      />
      <Navbar />

      <main className="pt-20">
        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  {article.readTime} 分钟阅读
                </span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  {article.publishedAt}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold prose-h2:text-xl md:prose-h2:text-2xl prose-h3:text-lg md:prose-h3:text-xl prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}